import { createContext, useCallback, useContext, useEffect, useMemo, useState, type ReactNode } from 'react';
import { PortalApiError, bootstrapAccount, getConsumerSession } from './api';
import {
  clearPortalCognitoSession,
  cognitoEnabled,
  currentPortalIdToken,
  loadPortalCognitoSession,
  startCognitoLogin,
  startCognitoLogout,
} from './cognito';

const SESSION_BOOTSTRAP_TIMEOUT_MS = 12_000;
const SESSION_UNAVAILABLE_MESSAGE = 'The secure PackProof session could not start. Check your connection and try again.';
const COGNITO_NOT_CONFIGURED_MESSAGE = 'Cognito web configuration is missing from this portal deployment.';

export type PortalUser = {
  uid: string;
  email: string | null;
  displayName: string | null;
};

type AuthState = {
  user: PortalUser | null;
  loading: boolean;
  enabled: boolean;
  sessionError: string | null;
  retrySession: () => void;
  signIn: (returnTo?: string) => Promise<void>;
  signOutUser: () => Promise<void>;
};

const AuthContext = createContext<AuthState | null>(null);

async function boundPortalUser(): Promise<PortalUser> {
  try {
    const session = await getConsumerSession();
    return { uid: session.data.actorId, email: session.data.email, displayName: session.data.displayName };
  } catch (error) {
    if (!(error instanceof PortalApiError) || error.code !== 'USER_IDENTITY_NOT_BOUND') throw error;
    const created = await bootstrapAccount(await currentPortalIdToken());
    return { uid: created.data.actorId, email: created.data.email, displayName: created.data.displayName };
  }
}

export function AuthProvider({ children }: { children: ReactNode }) {
  const enabled = cognitoEnabled();
  const [user, setUser] = useState<PortalUser | null>(null);
  const [loading, setLoading] = useState(enabled);
  const [sessionError, setSessionError] = useState<string | null>(enabled ? null : COGNITO_NOT_CONFIGURED_MESSAGE);
  const [sessionAttempt, setSessionAttempt] = useState(0);

  useEffect(() => {
    if (!enabled) return;
    let active = true;
    const timeout = window.setTimeout(() => {
      if (!active) return;
      setLoading(false);
      setSessionError(SESSION_UNAVAILABLE_MESSAGE);
    }, SESSION_BOOTSTRAP_TIMEOUT_MS);
    void (async () => {
      try {
        if (!loadPortalCognitoSession()) {
          if (!active) return;
          window.clearTimeout(timeout);
          setUser(null);
          setSessionError(null);
          setLoading(false);
          return;
        }
        const next = await boundPortalUser();
        if (!active) return;
        window.clearTimeout(timeout);
        setUser(next);
        setSessionError(null);
        setLoading(false);
      } catch {
        if (!active) return;
        window.clearTimeout(timeout);
        clearPortalCognitoSession();
        setUser(null);
        setSessionError(SESSION_UNAVAILABLE_MESSAGE);
        setLoading(false);
      }
    })();
    return () => {
      active = false;
      window.clearTimeout(timeout);
    };
  }, [enabled, sessionAttempt]);

  const retrySession = useCallback(() => {
    if (!enabled) return;
    setSessionError(null);
    setLoading(true);
    setSessionAttempt((attempt) => attempt + 1);
  }, [enabled]);

  const value = useMemo<AuthState>(() => ({
    user,
    loading,
    enabled,
    sessionError,
    retrySession,
    async signIn(returnTo = '/') {
      if (!enabled) throw new Error(COGNITO_NOT_CONFIGURED_MESSAGE);
      await startCognitoLogin(returnTo);
    },
    async signOutUser() {
      startCognitoLogout();
    },
  }), [enabled, loading, retrySession, sessionError, user]);

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
}

export function useAuth(): AuthState {
  const value = useContext(AuthContext);
  if (!value) throw new Error('useAuth must be used within AuthProvider.');
  return value;
}
